/**
 * Day 84.6 — PostgREST error translator.
 * Day 84.6.5 — KNOWN_COMPOSITE_CONSTRAINTS registry + _unique suffix regex extension.
 *
 * Pure function maps PostgrestError objects (from supabase-js direct writes —
 * Directory CRUD + Settings param edits) to UserError envelope shape per L5
 * translator path. RPC envelope path bypasses this; only PostgREST direct
 * writes use this helper.
 *
 * Handles 7 PostgreSQL/PostgREST error codes: 23505 / 23503 / 23514 / 23502 /
 * 42501 / PGRST100 / PGRST116 per Phase 1.3 disk-verified existing test
 * references (day64Rls + day71Schema + postgrestEscape).
 *
 * field_path extraction is best-effort: parses constraint name (23505/23503/
 * 23514) or column name (23502) from PostgreSQL error message. Falls back to
 * null on extraction ambiguity. Document heuristic limits in
 * COMPONENT_STANDARDS §21.
 *
 * Composite UNIQUE constraints route to field_path: null per ADR 45 +
 * COMPONENT_STANDARDS §21 "Toast vs FieldError discriminator" — composite
 * collisions have no single field owner semantically (the (col_a, col_b)
 * pair is the duplicate, not either column alone). Day 84.6.5 added the
 * KNOWN_COMPOSITE_CONSTRAINTS registry for explicit early-return; extend
 * additively when a new composite constraint surfaces.
 */

import type { PostgrestError } from '@supabase/supabase-js';
import type { UserError } from './errorCodes';

/**
 * Day 84.6.5 — explicit registry of composite UNIQUE constraint names that
 * should route to toast (field_path: null) rather than attempt a single-field
 * derivation. Disk-verified at Day 84.6.5 CC Round 1 gate 15 against
 * supabase/schema.sql composite UNIQUE inventory. Extend additively as new
 * composite constraints surface.
 */
const KNOWN_COMPOSITE_CONSTRAINTS = new Set<string>([
  'po_staging_user_id_product_id_key',
  'price_overrides_product_id_marketplace_key',
  'velocity_overrides_product_id_marketplace_key',
  'po_line_item_allocations_line_item_id_fulfillment_path_key',
  'buying_session_scenarios_unique_name_per_user_distributor',
  'inventory_items_unique_loc',
  'manufacturer_distributors_manufacturer_id_distributor_id_key',
  'notification_preferences_user_id_event_channel_key',
  'product_profitability_product_id_distributor_id_marketplace_key',
]);

/**
 * Parse constraint name from PostgreSQL error message of the form:
 *   'duplicate key value violates unique constraint "distributors_name_key"'
 *   'insert or update on table "X" violates foreign key constraint "X_brand_id_fkey"'
 *   'new row for relation "X" violates check constraint "X_check"'
 *
 * Strips table prefix + `_key` / `_fkey` / `_check` / `_pkey` / `_unique`
 * suffix to yield column name. Composite UNIQUE constraints in the
 * KNOWN_COMPOSITE_CONSTRAINTS registry early-return null (caller routes to
 * toast per §21 discriminator). Returns null on extraction ambiguity.
 */
function deriveFromConstraintName(msg: string): string | null {
  const match = msg.match(/constraint "([^"]+)"/);
  if (!match) return null;
  const name = match[1];
  // Day 84.6.5 — composite UNIQUE constraints route to toast (Q1=a)
  if (KNOWN_COMPOSITE_CONSTRAINTS.has(name)) return null;
  // Day 84.6.5 — _unique suffix family added (NN1) for partial-index composites
  const stripped = name.replace(/_(key|fkey|check|pkey|unique)$/, '');
  // Strip table prefix (everything up to and including the first underscore)
  const parts = stripped.split('_');
  if (parts.length < 2) return stripped || null;
  return parts.slice(1).join('_') || null;
}

/**
 * Parse column name from PostgreSQL not-null violation message:
 *   'null value in column "<col>" of relation "<table>" violates not-null constraint'
 */
function deriveColumnFromMessage(msg: string): string | null {
  const match = msg.match(/column "([^"]+)"/);
  return match ? match[1] : null;
}

export function translatePostgrestError(err: PostgrestError): UserError {
  const code = err.code;
  const message = err.message ?? '';
  switch (code) {
    case '23505':
      return {
        code: 'INPUT.INVALID',
        field_path: deriveFromConstraintName(message),
        message: 'Already exists',
      };
    case '23503':
      return {
        code: 'INPUT.INVALID',
        field_path: deriveFromConstraintName(message),
        message: 'Referenced record not found',
      };
    case '23514':
      return {
        code: 'INPUT.INVALID',
        field_path: deriveFromConstraintName(message),
        message: 'Value does not meet requirements',
      };
    case '23502':
      return {
        code: 'INPUT.REQUIRED',
        field_path: deriveColumnFromMessage(message),
        message: 'Required field',
      };
    case '42501':
      return {
        code: 'DIST_REP.ACCESS_DENIED',
        field_path: null,
        message: 'Access denied',
      };
    case 'PGRST100':
      return {
        code: 'INPUT.INVALID',
        field_path: null,
        message: 'Invalid request',
      };
    case 'PGRST116':
      return {
        code: 'INPUT.INVALID',
        field_path: null,
        message: 'Record not found or not modified',
      };
    default:
      return {
        code: 'INPUT.INVALID',
        field_path: null,
        message: message || 'Unknown error',
      };
  }
}
